"use client";

/**
 * The reading surface for a `resources/` folder, built around the one thing the
 * tool it replaces gets wrong: it decides what a pack contains and then hides
 * the working.
 *
 * `src/lib/gtaKnowledge/spawnIndex.ts` already does the analysis and already
 * refuses to drop anything — a pack with no codes comes back with the sentence
 * that explains the zero, a demoted name comes back in `filtered` with the
 * reason that demoted it, a parse failure comes back in the XML parser's own
 * words. None of that is worth anything if the screen renders `index.codes` and
 * stops. So the layout here is deliberately not "a list of spawn codes with some
 * diagnostics folded away underneath":
 *
 *   - Every pack handed in gets a heading, including the ones that produced
 *     nothing. Scrolling past `vendor-b — 0 codes` and its reason is a choice
 *     the reader makes; not finding `vendor-b` on the page at all is the prior
 *     art's answer and it is indistinguishable from "this pack is fine".
 *   - The demoted names are a top-level section with its own heading and its own
 *     count, not a disclosure triangle. The defect this tool exists to beat is a
 *     substring blacklist that ate `gtr_model` and `police_extra` with no way to
 *     see that it had; a panel nobody opens is the same defect with one more
 *     click in front of it. Every row in it has a Restore button, and every row
 *     restored can be put back.
 *   - Every caveat the read produced is on screen. Scan-level ones go through
 *     `FindingsList`'s shared skipped line; a caveat that belongs to one pack is
 *     printed under that pack's heading, because "this folder has no
 *     fxmanifest.lua" is only actionable next to the folder it is about.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE LISTS ARE BOUNDED AND NOT WINDOWED
 *
 * `VirtualGrid` is the house windowing component and it is the wrong shape for
 * this page twice over. It lays out a flat list of fixed-height cards, and this
 * is a grouped list — pack heading, note, caveats, rows — whose rows are as tall
 * as their badges wrap. And the window arithmetic is driven by a
 * `ResizeObserver`, which means a row count that depends on a measured viewport;
 * that is right for a catalogue you browse and wrong for a page whose output a
 * reader is going to Ctrl-F through.
 *
 * So this follows `ResmonReport.tsx` instead: every list draws a slice and
 * reveals further slices on request, never "all of them" behind one button,
 * which is the same freeze with a click in front of it. The rule that makes a
 * cap honest is the one that file states and this one keeps — **every count on
 * this page counts every row, and the exports carry every row**. A cap changes
 * how many are on screen at once and nothing else.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE READ IS CHEAP, AND WHY THAT CLAIM SURVIVES READING THE CODE
 *
 * `intake.ts` walks the drop and hands `collect.ts` a file list. `collect.ts`
 * opens a file only when its extension is in `TEXT_EXTENSIONS`, and every
 * `.yft`, `.ytd`, `.ydr` and archive is inventory: a path and a size, never a
 * byte. A 4 GB `resources/` folder is a few megabytes of text inside a directory
 * listing, so the scan costs about what enumerating the names costs.
 *
 * `TEXT_EXTENSIONS` is twelve extensions and not three, so the copy on the page
 * may not narrow it to three. It used to name `fxmanifest.lua`, `.meta` and
 * `.lua` and stop there, which is a narrower claim than the code makes: a server
 * owner who drags a whole server folder has every `server.cfg`, `.json`, `.js`
 * and `.sql` decoded into tab memory. So the intake paragraph says "text files"
 * and no list at all — the list is in the page's FAQ, and the claim on screen is
 * true of the code with none of it. Nothing is uploaded either way — there is no
 * `fetch`, `XMLHttpRequest`, `sendBeacon` or `WebSocket` in this page's import
 * graph — but a privacy sentence that is not true of the code is worth less than
 * none, because it is the one claim a reader is invited to check.
 *
 * This argument stays here and is no longer also on screen. A maintainer's
 * reason may be any length and lives in the source; a user's fact is short and
 * lives in the DOM.
 *
 * The figure under the drop zone is the checkable half of it, and it counts what
 * the collector actually decoded rather than what was eligible to be decoded.
 * `collect.ts` skips a text file over 4 MB and stops once 32 MB is decoded, so
 * an eligibility count overstates on exactly the folders where the caps bite.
 * Whatever the collector refused is printed beside it in its own words.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 * This renders one scan. It has no notion of adding a second folder to an
 * existing one, and that is deliberate: `collectResources` can only promise
 * unique identities *within one drop*, so unioning two picks is how two packs
 * called `police` become one and the report comes back clean. Picking again
 * replaces the scan.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  RotateCcw,
  Undo2,
  Upload,
} from "lucide-react";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { FindingsList, type FindingsFixResult } from "@/components/gta/FindingsList";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  steps as spawnIndexSteps,
  finishLabel as spawnIndexFinishLabel,
} from "@/components/walkthrough/steps/spawnIndex";
import { downloadTextFile } from "@/lib/dom";
import { collectResources, type CollectableGroup } from "@/lib/gtaKnowledge/collect";
import {
  DEMO_INPUT_NOTE,
  DEMO_STATE_LABEL,
  demoDownloadName,
  demoPacksFor,
  type DemoPack,
  type DemoPackUse,
} from "@/lib/gtaKnowledge/demoPacks";
import {
  fromArchives,
  fromDataTransfer,
  fromDirectoryPick,
  type DataTransferLike,
  type ResourceDrop,
} from "@/lib/gtaKnowledge/intake";
import { normalizeName, pathKey } from "@/lib/gtaKnowledge/normalize";
import {
  SPAWN_INDEX_SCOPE_NOTE,
  attachSpawnIndexFixes,
  buildSpawnIndex,
  restoreFiltered,
  spawnCodeKey,
  spawnIndexFindings,
  toQbSharedLua,
  toSpawnCsv,
  toSpawnText,
  toVehiclesLua,
  type SpawnBadge,
  type SpawnCode,
  type SpawnIndex,
  type SpawnPack,
  type SpawnTier,
} from "@/lib/gtaKnowledge/spawnIndex";
import type { GtaResourceInput } from "@/lib/gtaKnowledge/types";

// ─── Caps ────────────────────────────────────────────────────────────────────
//
// Exported so the test asserts against the numbers the page uses rather than
// against a copy of them, which is how a cap gets raised in one place and
// tested in another.

/** Pack sections drawn before the tail is held back. */
export const SPAWN_PACKS_VISIBLE = 25;

/** How many more pack sections one click mounts. Not "all of them". */
export const SPAWN_PACKS_PAGE = 25;

/** Code rows drawn inside one pack before its own reveal appears. */
export const SPAWN_CODES_VISIBLE = 50;

/** How many more code rows one click mounts inside a pack. */
export const SPAWN_CODES_PAGE = 200;

/** Demoted rows drawn in the filtered panel before its reveal appears. */
export const SPAWN_FILTERED_VISIBLE = 25;

/** How many more demoted rows one click mounts. */
export const SPAWN_FILTERED_PAGE = 100;

// ─── Vocabulary ──────────────────────────────────────────────────────────────

/**
 * The badges, spelled exactly as `spawnIndex.ts` spells them.
 *
 * That module keeps the same table privately for its text, CSV and Lua exports
 * and does not export it. Duplicated here *identically* rather than shortened:
 * a chip that reads `duplicate` above a `.txt` download that reads
 * `duplicate across packs` is the screen and the paste disagreeing about the
 * same fact, which is the class of bug this whole layer is written against. If
 * `spawnIndex.ts` ever exports its table, delete this one.
 */
const BADGE_LABELS: Record<SpawnBadge, string> = {
  duplicate: "duplicate across packs",
  vanilla: "replaces a base-game vehicle",
  emergency: "emergency fleet",
  "no-model": "no model file",
  "no-handling": "no handling entry",
  "no-variation": "no carvariations entry",
  unregistered: "meta not registered",
};

/**
 * What each badge means, in a sentence, once.
 *
 * A legend rather than a tooltip per row: `no-variation` is not self-evident and
 * a reader meeting it on row 40 of 300 should not have to hover to find out
 * whether it matters. These sentences are this page's own — the exports carry
 * the same *labels*, and the argument for each is in `spawnIndex.ts`.
 *
 * Twenty words each, and the reason is the whole point of a legend: seven
 * entries at 26 words is longer than one screen, so a reader scans three of
 * them and gives up — and the badge they gave up before reaching is the one on
 * the row in front of them. Nothing was dropped from the set; each sentence
 * makes the same claim in fewer words. What may never be shortened away is the
 * *cause* a badge has two of: see `no-handling`.
 */
const BADGE_MEANINGS: Record<SpawnBadge, string> = {
  duplicate:
    "Another resource declares this code too. The game keeps one model per name, so load order decides which appears.",
  vanilla:
    "The base game already owns this name, so this pack replaces that vehicle everywhere rather than adding one.",
  emergency:
    "An emergency fleet name: every unit on the server drives it, so one problem is everyone's problem.",
  "no-model": "No .yft of this name streams from this pack, so spawning the code produces nothing.",
  // Two causes, and the sentence covers both. Narrowed to the second one it
  // contradicted the caveat printed two lines above it on the same pack — "No
  // handling.meta was loaded" — which is the screen disagreeing with itself. So
  // the shortening keeps both causes and spends its words there.
  "no-handling":
    "Nothing binds this code to a handling entry: it names no handlingId, or none this pack defines. Default physics apply.",
  "no-variation":
    "The pack's carvariations.meta does not list this code, so its colours and mod kit are unbound.",
  unregistered:
    "The manifest never registers the vehicles.meta this code came from, so FiveM does not read it.",
};

/** Which badges are loud. `duplicate` and `emergency` are the two that break a live server. */
const LOUD_BADGES: ReadonlySet<SpawnBadge> = new Set<SpawnBadge>([
  "duplicate",
  "emergency",
  "no-model",
  "unregistered",
]);

/**
 * The tier, spelled as `spawnIndex.ts`'s `tierWord` spells it, for the reason
 * given above `BADGE_LABELS`.
 */
const TIER_LABELS: Record<SpawnTier, string> = {
  meta: "from vehicles.meta",
  guess: "guessed from file names",
  none: "nothing to read",
};

const CONFIDENCE_LABELS: Record<SpawnCode["confidence"], string> = {
  high: "high confidence",
  medium: "medium confidence",
  low: "low confidence",
};

// ─── Small helpers ───────────────────────────────────────────────────────────

function n(value: number): string {
  return value.toLocaleString("en-US");
}

function count(value: number, singular: string, many?: string): string {
  return `${n(value)} ${value === 1 ? singular : (many ?? `${singular}s`)}`;
}

/**
 * A byte figure for the one line that prints one.
 *
 * Local rather than `resmon.ts`'s `formatBytes`, which is the same arithmetic
 * behind a 1,500-line log parser: importing that module into a public page to
 * borrow a formatter would put the parser in the bundle of every visitor who
 * never drops a folder. This formats a display figure and decides no fact.
 */
function formatSize(bytes: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(2)} ${units[unit]}`;
}

// ─── Intake ──────────────────────────────────────────────────────────────────

/**
 * A drop's resources, with the attribution that keeps two packs two packs.
 *
 * `root` and `origin` are passed through, and that is the whole point of this
 * function existing rather than being three words inline. `intake.ts` produces a
 * unique root per resource and an origins map precisely so that two vendor packs
 * both called `police` survive as two; a caller that hands `collectResources`
 * only `{ name, files }` throws both away at the door, the two fold into one,
 * and the duplicate-across-packs finding — the single most valuable thing this
 * tool produces — cannot fire. Exported because that is the claim worth a test
 * of its own.
 */
export function groupsOf(drop: ResourceDrop): CollectableGroup[] {
  return drop.resources.map((resource) => {
    const origin = drop.origins.get(resource.root);
    return {
      name: resource.name,
      files: resource.files,
      root: resource.root,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
    };
  });
}

/** What one scan produced, before the reader has restored anything. */
export type SpawnScan = {
  /** The index as built. Restores are applied on top of this, never into it. */
  index: SpawnIndex;
  /** Kept for `attachSpawnIndexFixes` and for the manifest a one-click fix patches. */
  inputs: GtaResourceInput[];
  /** Everything intake and the collector could not read, in their own words. */
  notices: string[];
  /** Every path the drop held, including the ones no resource claimed. */
  files: number;
  /**
   * Files the collector actually decoded, counted off what it returned.
   *
   * Deliberately not "files whose extension made them eligible". `collect.ts`
   * skips a text file over 4 MB and stops opening text once 32 MB is decoded, so
   * on exactly the folders where the caps matter an eligibility count overstates
   * — and this figure exists to be the *checkable* version of the privacy claim
   * two lines above it. A figure that overstates there is worse than none.
   */
  opened: number;
  bytes: number;
};

/**
 * A `ResourceDrop`, read end to end.
 *
 * Exported so a test can drive the whole pipeline — intake, collection, index —
 * without a browser gesture, and so the page has exactly one path from a drop to
 * a scan whatever the gesture was.
 */
export async function scanDrop(drop: ResourceDrop): Promise<SpawnScan> {
  const { inputs, truncated } = await collectResources(groupsOf(drop));
  return {
    index: buildSpawnIndex(inputs),
    inputs,
    notices: [...new Set([...drop.truncated, ...truncated])],
    files: drop.allPaths.length,
    opened: inputs.reduce((total, input) => total + input.texts.size, 0),
    bytes: drop.allPaths.reduce((total, entry) => total + (entry.size || 0), 0),
  };
}

// ─── The page ────────────────────────────────────────────────────────────────

type Phase =
  | { kind: "idle" }
  | { kind: "reading" }
  | { kind: "ready"; scan: SpawnScan }
  | { kind: "failed"; message: string };

/**
 * The patched text of a one-click fix, which a page that writes nothing hands
 * back as a file.
 *
 * `file` is the resource-qualified path the edit named, because that is the only
 * form that says *which* `fxmanifest.lua` was patched. `name` is what the
 * download is called, which cannot carry a directory, so the sentence on screen
 * carries the pack and the filename carries the rest.
 */
type Patch = { file: string; name: string; text: string; applied: number; rejected: number };

/** `vendor-b/fxmanifest.lua` → `fxmanifest.lua`, for a download that cannot hold a path. */
function fileNameOf(path: string): string {
  const slash = path.replace(/\\/g, "/").lastIndexOf("/");
  return slash === -1 ? path : path.slice(slash + 1);
}

export function SpawnIndexClient() {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [restored, setRestored] = useState<readonly string[]>([]);
  const [query, setQuery] = useState("");
  const [packsShown, setPacksShown] = useState(SPAWN_PACKS_VISIBLE);
  const [codesShown, setCodesShown] = useState<Record<string, number>>({});
  const [filteredShown, setFilteredShown] = useState(SPAWN_FILTERED_VISIBLE);
  const [patch, setPatch] = useState<Patch | null>(null);
  const [dragging, setDragging] = useState(false);
  /**
   * Whether what is on screen came from a built-in sample pack.
   *
   * An argument of the read rather than a mode, so real input clears it with no
   * code remembering to: every gesture goes through `begin`, and `begin`'s
   * default is false. While set, `DEMO_INPUT_NOTE` joins the same list the scope
   * note travels in and every download is renamed.
   */
  const [demo, setDemo] = useState(false);
  /** Raised by the demo button only. See the mount at the bottom of this file. */
  const [tourSignal, setTourSignal] = useState(0);
  const abort = useRef<AbortController | null>(null);

  const scan = phase.kind === "ready" ? phase.scan : null;

  const begin = useCallback(async (
    read: (signal: AbortSignal) => Promise<ResourceDrop>,
    fromDemo = false,
  ) => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setPhase({ kind: "reading" });
    setDemo(fromDemo);
    setRestored([]);
    setQuery("");
    setPacksShown(SPAWN_PACKS_VISIBLE);
    setCodesShown({});
    setFilteredShown(SPAWN_FILTERED_VISIBLE);
    setPatch(null);
    try {
      const next = await scanDrop(await read(controller.signal));
      if (controller.signal.aborted) return;
      setPhase({ kind: "ready", scan: next });
    } catch (error) {
      if (controller.signal.aborted) return;
      setPhase({
        kind: "failed",
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  // ── The index the page renders ────────────────────────────────────────────
  //
  // Restores are re-applied to the *base* index on every change rather than
  // accumulated into it, because `restoreFiltered` rebuilds the duplicate
  // badges, the cross-pack findings and the counts from scratch: a name restored
  // in one pack can be the second half of a collision in another, and an index
  // whose rows moved but whose badges did not is exactly the quiet disagreement
  // this layer is written against.
  const index = useMemo(() => {
    if (!scan) return null;
    return restored.length > 0 ? restoreFiltered(scan.index, restored) : scan.index;
  }, [scan, restored]);

  const findings = useMemo(() => {
    if (!index || !scan) return [];
    return attachSpawnIndexFixes(spawnIndexFindings(index), scan.inputs);
  }, [index, scan]);

  /**
   * The index as it is exported, which differs from the one on screen by one
   * sentence and only while a demo is loaded.
   *
   * `toSpawnText` and `toSpawnCsv` both print `index.skipped` under "Not
   * checked", so adding the demo sentence to that list is what carries the fact
   * into the files a reader keeps.
   *
   * The two Lua exports do not read `skipped` — their preamble is
   * `spawnIndexSourceStamp`, built inside the library from the index alone — so
   * they were leaving with the filename as their only signal. That was the
   * wrong export to leave silent: a `vehicles.lua` is the one file here that
   * gets pasted straight into a running resource, at which point the filename
   * is gone and the contents are all that is left. Both functions already emit
   * `--` comment lines, so the channel existed; `demoPreamble` below is the
   * client putting the sentence into it, because the library cannot see that
   * the index came from a sample.
   */
  const exportIndex = useMemo(() => {
    if (!index) return null;
    if (!demo) return index;
    return { ...index, skipped: [...new Set([...index.skipped, DEMO_INPUT_NOTE])] };
  }, [index, demo]);

  /**
   * The disk, which this page does not have.
   *
   * A free, read-only page holds the text of the files the reader dropped and
   * nothing else, so a one-click fix produces a patched file the reader
   * downloads.
   *
   * Which file is the whole question here. Two resources in one scan can both
   * hold `fxmanifest.lua`, so `spawnIndex.ts` qualifies every edit it produces
   * with the resource's identity — `vendor-b/fxmanifest.lua` — and this resolves
   * that back against the resource of that identity and no other. An unqualified
   * path is still accepted, because a finding from elsewhere in the layer may
   * carry one, and *that* is the case that refuses rather than guessing when two
   * packs hold the name: guessing would write one vendor's repair into the other
   * vendor's manifest and report it as a success.
   */
  const fixes = useMemo(() => {
    if (!scan) return undefined;
    return {
      readFile: (file: string): string | null => {
        const key = pathKey(file);
        for (const input of scan.inputs) {
          const prefix = `${pathKey(input.identity ?? input.name)}/`;
          if (!key.startsWith(prefix)) continue;
          const within = key.slice(prefix.length);
          if (input.texts.has(within)) return input.texts.get(within) ?? null;
        }
        const holders = scan.inputs.filter((input) => input.texts.has(key));
        if (holders.length > 1) {
          throw new Error(
            `${holders.length} resources in this scan hold a file called ${file}, and the finding ` +
              `does not say which one, so nothing was changed. Scan that pack on its own`,
          );
        }
        return holders[0]?.texts.get(key) ?? null;
      },
      onApplied: (result: FindingsFixResult) => {
        setPatch({
          file: result.file,
          name: fileNameOf(result.file),
          text: result.text,
          applied: result.applied.length,
          rejected: result.rejected.length,
        });
      },
    };
  }, [scan]);

  // ── Filtering, which changes what is drawn and never what is counted ───────

  const needle = normalizeName(query.trim());
  const packs = useMemo(() => {
    if (!index) return [];
    if (needle === "") return index.packs.map((pack) => ({ pack, codes: pack.codes }));
    const matches: { pack: SpawnPack; codes: SpawnCode[] }[] = [];
    for (const pack of index.packs) {
      // A pack whose own name matches keeps all of its codes: somebody who typed
      // `vendor-b` wants the pack, not the subset of its codes that happen to
      // contain the string `vendor-b`.
      const byName = normalizeName(pack.label).includes(needle);
      const codes = byName
        ? pack.codes
        : pack.codes.filter((code) => normalizeName(code.name).includes(needle));
      if (byName || codes.length > 0) matches.push({ pack, codes });
    }
    return matches;
  }, [index, needle]);

  const matchingCodes = packs.reduce((total, entry) => total + entry.codes.length, 0);

  // ── The demoted names, read off the base index so an undo has somewhere to go ──

  const restoredKeys = useMemo(() => new Set(restored), [restored]);
  const demoted = useMemo(() => {
    if (!scan) return [] as { pack: SpawnPack; code: SpawnCode; key: string }[];
    return scan.index.packs.flatMap((pack) =>
      pack.filtered.map((code) => ({ pack, code, key: spawnCodeKey(code) })),
    );
  }, [scan]);
  const pending = demoted.filter((entry) => !restoredKeys.has(entry.key));
  const putBack = demoted.filter((entry) => restoredKeys.has(entry.key));

  const restore = useCallback((key: string) => {
    setRestored((current) => (current.includes(key) ? current : [...current, key]));
  }, []);
  const undo = useCallback((key: string) => {
    setRestored((current) => current.filter((entry) => entry !== key));
  }, []);

  // ── Gestures ──────────────────────────────────────────────────────────────

  const onFolder = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromDirectoryPick(files, { signal }));
  };
  const onArchives = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void begin((signal) => fromArchives(Array.from(files), { signal }));
  };
  const onDrop = (dt: DataTransferLike | null) => {
    setDragging(false);
    if (!dt) return;
    void begin((signal) => fromDataTransfer(dt, { signal }));
  };

  /**
   * A sample pack, fed through the gesture its registration names.
   *
   * Through `begin` and therefore through `intake.ts`, because the layer this
   * tool is mostly about is the one that decides a folder declaring nothing is
   * two resources rather than one synthetic root. A demo that called
   * `buildSpawnIndex` directly would be a second path that can pass while the
   * real one is broken, and it could not show the thing the pack is for.
   *
   * The tour opens after the read resolves, so its steps have real packs and a
   * real guessed-code badge to point at.
   */
  const demoPacks = useMemo(() => demoPacksFor("spawn-index"), []);
  const loadDemo = useCallback(
    async (pack: DemoPack, use: DemoPackUse) => {
      if (use.gesture === "archives") {
        const files = await pack.archives();
        await begin((signal) => fromArchives(files, { signal }), true);
      } else {
        const list = pack.pick();
        await begin((signal) => fromDirectoryPick(list, { signal }), true);
      }
      setTourSignal((signal) => signal + 1);
    },
    [begin],
  );

  /** Prefixed while a demo is loaded; see `demoDownloadName`. */
  const exportName = useCallback((name: string) => (demo ? demoDownloadName(name) : name), [demo]);

  /**
   * The demo sentence as a Lua comment, for the two exports that have no
   * `skipped` channel to carry it.
   *
   * Empty while no demo is loaded, so a real export is byte-identical to what
   * the library produced. `luaComment`'s job in the library is stripping
   * newlines out of pack notes; `DEMO_INPUT_NOTE` is one line by construction,
   * so nothing here has to repeat that work.
   */
  const demoPreamble = demo ? `-- ${DEMO_INPUT_NOTE}\n` : "";

  return (
    <div className="space-y-8">
      {/* ── Intake ──────────────────────────────────────────────────────── */}
      <section
        data-testid="spawn-index-drop"
        data-walkthrough="spi-drop"
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          onDrop(event.dataTransfer as unknown as DataTransferLike);
        }}
        className={`not-prose rounded-2xl border-2 border-dashed p-5 transition-colors sm:p-6 ${
          dragging ? "border-primary/60 bg-primary/5" : "border-border/70 bg-muted/30"
        }`}
      >
        <h2 className="text-base font-semibold text-foreground">
          Drop a resource, a <code className="font-mono">resources/</code> folder, or a zip
        </h2>
        {/*
          Two claims and no third one, because this is the paragraph a
          first-time visitor reads before acting: what gets opened, and that
          nothing is uploaded.
          It says "text files" rather than naming three extensions, and the
          wording is load-bearing: `TEXT_EXTENSIONS` is twelve extensions,
          `.cfg`, `.json`, `.js` and `.sql` among them, so the old sentence
          naming `fxmanifest.lua`, `.meta` and `.lua` was false of a dropped
          server folder — every `server.cfg` decoded into tab memory while the
          page told the reader otherwise. A privacy sentence that is not true of
          the code is worth less than none.
          What is NOT here any more: why the read is cheap ("a 4 GB folder costs
          about what listing it costs") and the full extension list. The first is
          this tool arguing its own case at a reader who is trying to drop a
          folder, and the argument lives above at WHY THE READ IS CHEAP, for the
          maintainer who can act on it. The second survives at greater length in
          the page's own FAQ (`src/lib/freeTools/seo.ts`), below the tool.
        */}
        <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground">
          Only text files are opened, each up to 4 MB. Binary assets are counted by name and size
          and never read. <strong className="text-foreground">Nothing is uploaded.</strong>
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
            <FolderOpen className="h-4 w-4" />
            Pick a folder
            <input
              data-testid="spawn-index-folder"
              type="file"
              multiple
              className="sr-only"
              onChange={(event) => onFolder(event.target.files)}
              {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40">
            <Upload className="h-4 w-4" />
            Pick zips
            <input
              data-testid="spawn-index-archives"
              type="file"
              multiple
              accept=".zip,.rar,.7z"
              className="sr-only"
              onChange={(event) => onArchives(event.target.files)}
            />
          </label>
          {/* In the intake row and gated on nothing — see `ShowTourButton`. The
              sample-pack panel below disappears once a report exists, so it
              cannot be the only way in, and the user who brought their own
              folder is the one who needed a tutorial most. */}
          <ShowTourButton
            testId="spawn-index-show-tour"
            onClick={() => setTourSignal((signal) => signal + 1)}
          />
        </div>

        {/* ── The worked example, in the empty state only ──
            One button per registered use, labelled with the finding it is about
            to produce. They stop being rendered the moment a scan exists, which
            is how "a demo may never replace input the user loaded" is kept with
            no hidden state and no confirm dialog. */}
        {phase.kind !== "ready" && demoPacks.length > 0 && (
          <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-4">
            <p className="text-xs font-medium text-foreground">
              No folder to hand? Load a sample pack and read a real index.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {demoPacks.map(({ pack, use }) => (
                <button
                  key={pack.id}
                  type="button"
                  data-testid="spawn-index-demo"
                  disabled={phase.kind === "reading"}
                  onClick={() => void loadDemo(pack, use)}
                  className="max-w-full rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:border-primary/40 disabled:opacity-50"
                >
                  <span className="block text-foreground">{pack.label}</span>
                  <span className="mt-0.5 block font-normal leading-5 text-muted-foreground">
                    {use.teaches}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Beside the scan count, where a reader looks to find out what was
            read. Never behind a disclosure: a sentence a reader has to open
            something to see has not been told. */}
        {scan && demo && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              data-testid="spawn-index-demo-state"
              className="inline-flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1.5 text-xs font-medium text-foreground"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
              {DEMO_STATE_LABEL}
            </span>
          </div>
        )}

        {phase.kind === "reading" && (
          <p className="mt-4 text-sm text-muted-foreground">Reading the folder…</p>
        )}
        {phase.kind === "failed" && (
          <p className="mt-4 flex items-start gap-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>That folder could not be read: {phase.message}. Nothing was scanned.</span>
          </p>
        )}
        {scan && (
          <p className="mt-4 text-xs leading-6 text-muted-foreground">
            {count(scan.files, "file")} seen, {formatSize(scan.bytes)} in total.{" "}
            {count(scan.opened, "of them was", "of them were")} opened and decoded as text; the rest
            were listed by name and size and never read. Anything the reader refused for size is in
            the caveats below.
          </p>
        )}
      </section>

      {index && scan && (
        <>
          {/* ── The figures. Every one counts every row. ────────────────── */}
          <section
            aria-label="Totals"
            data-walkthrough="spi-totals"
            className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6"
          >
            <Figure label="Resources" value={n(index.counts.packs)} />
            <Figure label="Spawn codes" value={n(index.counts.codes)} />
            <Figure label="From vehicles.meta" value={n(index.counts.fromMeta)} />
            <Figure
              label="Guessed"
              value={n(index.counts.guessed)}
              tone="warn"
              note="read off file names, not meta"
            />
            <Figure
              label="Duplicated across packs"
              value={n(index.counts.duplicated)}
              tone={index.counts.duplicated > 0 ? "bad" : "plain"}
            />
            <Figure
              label="Packs with no codes"
              value={n(index.counts.empty)}
              tone={index.counts.empty > 0 ? "warn" : "plain"}
            />
          </section>

          {/* ── Exports ─────────────────────────────────────────────────── */}
          <section
            aria-label="Exports"
            data-walkthrough="spi-exports"
            className="flex flex-wrap gap-2"
          >
            <ExportButton
              icon={FileText}
              label="Copy list (.txt)"
              onClick={() =>
                downloadTextFile(
                  exportName("spawn-codes.txt"),
                  toSpawnText(exportIndex ?? index),
                  "text/plain",
                )
              }
            />
            <ExportButton
              icon={FileSpreadsheet}
              label="Spreadsheet (.csv)"
              onClick={() =>
                downloadTextFile(
                  exportName("spawn-codes.csv"),
                  toSpawnCsv(exportIndex ?? index),
                  "text/csv",
                )
              }
            />
            <ExportButton
              icon={Download}
              label="vehicles.lua"
              onClick={() =>
                downloadTextFile(
                  exportName("vehicles.lua"),
                  demoPreamble + toVehiclesLua(exportIndex ?? index),
                  "text/plain",
                )
              }
            />
            <ExportButton
              icon={Download}
              label="QBCore shared/vehicles.lua"
              onClick={() =>
                downloadTextFile(
                  exportName("qb-shared-vehicles.lua"),
                  demoPreamble + toQbSharedLua(exportIndex ?? index),
                  "text/plain",
                )
              }
            />
          </section>

          {/* ── Findings, through the shared surface ────────────────────── */}
          {/*
            `SPAWN_INDEX_SCOPE_NOTE` is in `truncated` and not in a paragraph of
            its own any more. It was a 52-word grey block under the six-tile
            grid, in the same size and colour as the explanation above it and
            qualifying an unstated subset of the tiles — which is the shape of
            text a reader skips, and the claim that most needs to land was the
            one register that guaranteed it would not.
            It is a claim about the whole scan, so by the rule in the copy spec
            it belongs in the one place this layer already keeps scan-level
            honesty: `FindingsList`'s `Not checked:` region, beside everything
            else intake and the collector refused. Still in words, still on
            screen, never behind a disclosure — and pinned on screen by
            `SpawnIndexClient.test.tsx` as well as in the export.
          */}
          <section aria-label="Problems" data-walkthrough="spi-findings">
            <FindingsList
              findings={findings}
              /* `DEMO_INPUT_NOTE` joins the same list rather than being rendered
                 beside it: one line puts the sentence in the not-checked region
                 on screen, which is the register this layer keeps scan-level
                 honesty in, without anything else learning that a demo exists. */
              truncated={[
                ...scan.notices,
                ...index.skipped,
                SPAWN_INDEX_SCOPE_NOTE,
                ...(demo ? [DEMO_INPUT_NOTE] : []),
              ]}
              fixes={fixes}
              emptyLabel="No spawn-code problem was found in the checks that ran."
            />
            {patch && (
              <div className="mt-3 rounded-xl border border-border/60 bg-card p-4 text-sm">
                <p className="text-foreground">
                  {patch.file} was patched in this tab — {count(patch.applied, "edit")} applied
                  {patch.rejected > 0 ? `, ${count(patch.rejected, "edit")} refused` : ""}. This
                  page cannot write to your disk, so download it and replace that exact file yourself — the
                  path above names which pack it belongs to.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    downloadTextFile(exportName(patch.name), patch.text, "text/plain")
                  }
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                >
                  <Download className="h-3.5 w-3.5" />
                  {/* The label is the name the file actually takes, prefix and
                      all. A button that promises `fxmanifest.lua` and delivers
                      `sample-fxmanifest.lua` is a small lie in the one place a
                      reader is about to act on the answer. */}
                  Download {exportName(patch.name)}
                </button>
              </div>
            )}
          </section>

          {/* ── The codes, by pack ──────────────────────────────────────── */}
          <section
            aria-label="Spawn codes by resource"
            data-walkthrough="spi-packs"
            className="space-y-4"
          >
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-foreground">
                  Spawn codes by resource
                </h2>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="spawn-index-scope">
                  {needle === ""
                    ? `${count(index.counts.codes, "code")} across ${count(index.counts.packs, "resource")}.`
                    : `${n(matchingCodes)} of ${count(index.counts.codes, "code")} match “${query.trim()}”, in ${count(packs.length, "resource")}.`}
                </p>
              </div>
              <label className="text-xs text-muted-foreground">
                <span className="sr-only">Find a code or a resource</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setPacksShown(SPAWN_PACKS_VISIBLE);
                  }}
                  placeholder="Find a code or a resource"
                  spellCheck={false}
                  className="w-64 max-w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground outline-none transition-colors focus:border-primary/50"
                />
              </label>
            </div>

            <BadgeLegend />

            {packs.slice(0, packsShown).map(({ pack, codes }) => {
              const shown = codesShown[pack.identity] ?? SPAWN_CODES_VISIBLE;
              const remaining = codes.length - shown;
              return (
                <article
                  key={pack.identity}
                  data-testid="spawn-index-pack"
                  className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5"
                >
                  <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <h3 className="font-mono text-sm font-semibold text-foreground">
                      {pack.label}
                    </h3>
                    <Chip tone={pack.tier === "meta" ? "good" : pack.tier === "guess" ? "warn" : "plain"}>
                      {TIER_LABELS[pack.tier]}
                    </Chip>
                    <span className="text-xs text-muted-foreground">
                      {count(pack.codes.length, "code")}
                      {needle === "" ? "" : ` · ${n(codes.length)} matching`}
                    </span>
                  </header>

                  {/* Present on every pack that has something left to say, and
                      the qualification is the point.

                      `pack.note` opens with the tier, the count and the source
                      — which the header three lines above has already drawn as
                      a chip and a number, so a clean `meta` pack printed
                      "12 spawn codes read from 1 vehicles.meta file, which is
                      where the game reads them from" directly under
                      "vendor-a-police · from vehicles.meta · 12 codes". Once
                      per pack, and the input this tool asks for is a whole
                      `resources/` folder, so forty resources meant forty
                      restatements.

                      Only that headline goes, and only where the header
                      carries the same fact: a `meta`-tier pack with no
                      caveats. Every caveat still renders verbatim — an unread
                      `vehicles.meta`, a meta that is not registered in the
                      manifest, a guess that could be incomplete, a name the
                      user restored by hand — because each of those narrows
                      what the list in front of them means, and this island is
                      not allowed to go quiet about that. */}
                  {!(pack.tier === "meta" && pack.noteCaveats.length === 0) && (
                    <p className="mt-2 text-xs leading-6 text-muted-foreground">{pack.note}</p>
                  )}

                  {/* A caveat about one pack, printed under that pack. */}
                  {pack.skipped.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {pack.skipped.map((text) => (
                        <li
                          key={text}
                          className="flex gap-2 text-xs leading-6 text-muted-foreground"
                        >
                          <AlertTriangle className="mt-1.5 h-3 w-3 shrink-0 text-warning" />
                          <span>Not checked: {text}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {codes.length > 0 && (
                    <ul className="mt-3 divide-y divide-border/50">
                      {codes.slice(0, shown).map((code) => (
                        <CodeRow key={spawnCodeKey(code)} code={code} />
                      ))}
                    </ul>
                  )}

                  {remaining > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setCodesShown((current) => ({
                          ...current,
                          [pack.identity]: shown + SPAWN_CODES_PAGE,
                        }))
                      }
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                      Show {n(Math.min(remaining, SPAWN_CODES_PAGE))} more of {n(codes.length)}
                    </button>
                  )}
                </article>
              );
            })}

            {packs.length > packsShown && (
              <button
                type="button"
                onClick={() => setPacksShown((current) => current + SPAWN_PACKS_PAGE)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
              >
                <ChevronRight className="h-4 w-4" />
                Show {n(Math.min(packs.length - packsShown, SPAWN_PACKS_PAGE))} more of{" "}
                {count(packs.length, "resource")}
              </button>
            )}

            {packs.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No resource in this scan matches “{query.trim()}”. Every one of the{" "}
                {count(index.counts.packs, "resource")} read is still in the exports.
              </p>
            )}
          </section>

          {/* ── The demoted names. A section, not a disclosure triangle. ── */}
          {demoted.length > 0 && (
            <section aria-label="Names seen and not listed" className="space-y-3">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                Seen and not listed ({n(pending.length)})
              </h2>
              <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
                Every name this tool saw and chose not to call a spawn code, with the reason it was
                demoted. Nothing is deleted and nothing is hidden: the ranking can be wrong —{" "}
                <code className="font-mono text-foreground">gtr_model</code> and{" "}
                <code className="font-mono text-foreground">police_extra</code> are real vehicles
                that read like mod-shop parts — so any row here can be put back into the list, the
                exports and the counts.
              </p>

              {putBack.length > 0 && (
                <div
                  data-testid="spawn-index-restored"
                  className="rounded-2xl border border-primary/30 bg-primary/5 p-4"
                >
                  <h3 className="text-sm font-semibold text-foreground">
                    Restored by hand ({n(putBack.length)})
                  </h3>
                  <ul className="mt-2 divide-y divide-border/40">
                    {putBack.map((entry) => (
                      <li
                        key={entry.key}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                      >
                        <code className="font-mono text-sm text-foreground">{entry.code.name}</code>
                        <span className="text-xs text-muted-foreground">{entry.pack.label}</span>
                        <button
                          type="button"
                          onClick={() => undo(entry.key)}
                          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                        >
                          <Undo2 className="h-3.5 w-3.5" />
                          Undo
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <ul className="divide-y divide-border/50 rounded-2xl border border-border/60 bg-card">
                {pending.slice(0, filteredShown).map((entry) => (
                  <li key={entry.key} data-testid="spawn-index-demoted" className="p-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <code className="font-mono text-sm text-foreground">{entry.code.name}</code>
                      <span className="text-xs text-muted-foreground">{entry.pack.label}</span>
                      <Chip tone="plain">{CONFIDENCE_LABELS[entry.code.confidence]}</Chip>
                      <button
                        type="button"
                        onClick={() => restore(entry.key)}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Add to the list
                      </button>
                    </div>
                    <p className="mt-1.5 text-xs leading-6 text-muted-foreground">
                      {entry.code.reason}
                    </p>
                  </li>
                ))}
              </ul>

              {pending.length > filteredShown && (
                <button
                  type="button"
                  onClick={() => setFilteredShown((current) => current + SPAWN_FILTERED_PAGE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30"
                >
                  <ChevronRight className="h-4 w-4" />
                  Show {n(Math.min(pending.length - filteredShown, SPAWN_FILTERED_PAGE))} more of{" "}
                  {n(pending.length)}
                </button>
              )}
            </section>
          )}
        </>
      )}

      {/* Manual mode. The signal is raised by the demo button and by the replay
          button beside the badge — never by a report appearing, because a
          report appearing cannot tell this component whose folder it came
          from, and a dimmer over a stranger's own results is an interruption. */}
      <ToolWalkthrough
        /* The sample-announcing first step is dropped when no sample is
           loaded, and this is an honesty fix rather than a tidy-up. That step
           says "not your files" — true over a demo, and a straightforward
           falsehood over a folder the reader picked themselves, which the
           permanent "Show me how this works" control now makes reachable.
           Filtered by `id` and not by index so reordering the array cannot
           silently drop a different step. Nothing is lost on the demo path,
           and nothing is claimed on the real one. */
        steps={demo ? spawnIndexSteps : spawnIndexSteps.filter((step) => step.id !== "welcome")}
        storageKey="workshop_tour_spawn_index_v1"
        finishLabel={spawnIndexFinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/**
 * One figure, and the caveat that belongs to that figure rather than to the page.
 *
 * `note` is the whole of what this component gained. A claim about one number
 * has to be readable by anyone who reads the number, which a paragraph under the
 * grid is not: it cannot say which tile it qualifies, it is set in the same grey
 * as the domain explanation two inches above it, and it is long enough to skip.
 * `StreamBudgetClient`'s `wastedFigure` already proves the alternative by
 * putting "not compared" where a figure would go; this is the same move for a
 * number that is real but means less than its label suggests.
 *
 * Kept short on purpose — six words is the budget — because a caption that
 * wraps to three lines is the paragraph again. Not a tooltip: an honesty claim
 * behind a hover does not exist on a touch device, which is the same argument
 * `BADGE_MEANINGS` is a legend rather than a per-row hover.
 */
function Figure({
  label,
  value,
  tone = "plain",
  note,
}: {
  label: string;
  value: string;
  tone?: "plain" | "warn" | "bad";
  note?: string;
}) {
  const colour =
    tone === "bad" ? "text-destructive" : tone === "warn" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-xl border border-border/60 bg-card px-4 py-3">
      <div className={`font-mono text-xl font-semibold ${colour}`}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {note !== undefined && <div className="mt-0.5 text-[11px] text-muted-foreground">{note}</div>}
    </div>
  );
}

function Chip({
  children,
  tone = "plain",
}: {
  children: ReactNode;
  tone?: "plain" | "good" | "warn" | "bad";
}) {
  const colour =
    tone === "bad"
      ? "border-destructive/40 text-destructive"
      : tone === "warn"
        ? "border-warning/40 text-warning"
        : tone === "good"
          ? "border-success/40 text-success"
          : "border-border text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] leading-5 ${colour}`}
    >
      {children}
    </span>
  );
}

function ExportButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Download;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:bg-muted/40"
    >
      <Icon className="h-4 w-4 text-primary/80" />
      {label}
    </button>
  );
}

/** What the badges mean, said once rather than hovered for on row 40 of 300. */
function BadgeLegend() {
  const badges = Object.keys(BADGE_LABELS) as SpawnBadge[];
  return (
    <details className="group rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <span>What the badges mean</span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>
      <dl className="mt-3 space-y-2">
        {badges.map((badge) => (
          <div key={badge} className="flex flex-wrap items-baseline gap-2">
            <dt>
              <Chip tone={LOUD_BADGES.has(badge) ? "bad" : "warn"}>{BADGE_LABELS[badge]}</Chip>
            </dt>
            <dd className="flex-1 text-xs leading-6 text-muted-foreground">
              {BADGE_MEANINGS[badge]}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * One spawn code.
 *
 * The confidence chip is on the guessed rows only, because `meta` codes are
 * always `high` and a chip that says the same thing on every row of a pack is a
 * chip a reader stops seeing. The row's `reason` is printed for a guess — it is
 * the argument the reader is being asked to accept or overrule — and the source
 * file stands in for it on a `meta` row, where "which file said so" is the whole
 * of how we know.
 */
function CodeRow({ code }: { code: SpawnCode }) {
  const trailer = [
    // A `gameName` nothing registers is a lookup key a player sees raw, and what
    // may be said about *why* depends on whether every Lua file was read: after
    // the label scan's cap, "not registered" is a claim about files nobody
    // opened. Same two sentences `spawnIndex.ts` writes into the exports.
    code.displayName ??
      (code.gameName === null
        ? null
        : `${code.gameName} (${
            code.labelScanComplete
              ? "label not registered"
              : "no AddTextEntry found in the Lua files that were read"
          })`),
    code.makeName ?? code.make,
  ].filter((part): part is string => typeof part === "string" && part !== "");

  return (
    <li data-testid="spawn-index-code" className="py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <code className="font-mono text-sm text-foreground">{code.name}</code>
        {code.tier === "guess" && (
          <Chip tone={code.confidence === "low" ? "warn" : "plain"}>
            guessed, {CONFIDENCE_LABELS[code.confidence]}
          </Chip>
        )}
        {code.badges.map((badge) => (
          <Chip key={badge} tone={LOUD_BADGES.has(badge) ? "bad" : "warn"}>
            {BADGE_LABELS[badge]}
          </Chip>
        ))}
        {trailer.length > 0 && (
          <span className="text-xs text-muted-foreground">{trailer.join(" · ")}</span>
        )}
      </div>
      <p className="mt-0.5 text-[11px] leading-5 text-muted-foreground">
        {code.tier === "guess" ? code.reason : code.source}
      </p>
    </li>
  );
}
